import { Button } from '@televet/kibble-ui/build/components/Button';
import { Menu, MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import { Tag } from '@televet/kibble-ui/build/components/Tag';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Flex } from '@televet/kibble-ui/build/chakra';
import React, { useMemo, useState } from 'react';
import { SpeciesType } from 'shared/types/graphql';
import { ConditionProps } from '../types/conditionalSegments.types';
import { SegmentWrapper } from './SegmentWrapper';

type PetSpeciesSegmentConditionValue = {
  species: string[];
};

type PetSpeciesSegmentProps = ConditionProps<PetSpeciesSegmentConditionValue>;

export const PetSpeciesSegment = ({
  onDelete,
  updateConditionValue,
  conditionValue,
  asSummary,
  displayValidation,
}: PetSpeciesSegmentProps): JSX.Element => {
  const [menuItems, setMenuItems] = useState(
    Object.values(SpeciesType)?.map((speciesType) => ({
      label: speciesType,
      value: speciesType,
      isSelected: !!conditionValue?.species?.includes(speciesType),
    })),
  );

  const selectedSpecies = useMemo(() => menuItems.filter((item) => item.isSelected), [menuItems]);

  const handleSelection = ({ value, isSelected }: MenuItemProps): void => {
    setMenuItems((currentItems) => {
      const species = [];
      const selectedItems = [];
      for (const item of currentItems) {
        const updatedItem = { ...item };
        if (item.value === value) {
          updatedItem.isSelected = !!isSelected;
        }
        if (updatedItem.isSelected) {
          species.push(item.value);
        }
        selectedItems.push(updatedItem);
      }
      updateConditionValue({ species });
      return selectedItems;
    });
  };

  const handleAllSelection = (selectedItems: MenuItemProps[]): void => {
    setMenuItems((currentItems) => currentItems.map((item) => ({ ...item, isSelected: selectedItems.length > 0 })));
    updateConditionValue({ species: selectedItems.map((i) => i.value || '') });
  };

  if (asSummary) {
    return (
      <Text fontWeight="light" color="text.subtle">
        Patient Species: {conditionValue.species.join(', ')}
      </Text>
    );
  }

  return (
    <SegmentWrapper onDelete={onDelete}>
      <Flex flexDir="column" gap="2">
        <Flex align="center" gap="3" w="100%">
          <Text fontWeight="bold" mr="3">
            Patient Species
          </Text>
          <Menu
            buttonProps={{
              size: 'sm',
              variant: 'secondarySubtle',
              leftIconName: 'plus',
              text: 'Add',
              showRightIcon: false,
            }}
            listProps={{
              isMultiSelect: true,
              isSearchable: true,
              canSelectAll: true,
              onSelectAll: handleAllSelection,
              onSelect: handleSelection,
              menuItems,
            }}
          />
          <Button
            variant="ghostNeutral"
            iconName="arrowCircle"
            size="sm"
            onClick={(): void => {
              handleAllSelection([]);
            }}
          >
            Clear All
          </Button>
        </Flex>

        {displayValidation && (
          <Text as="p" color="text.danger">
            Please add at least one species.
          </Text>
        )}

        {selectedSpecies.length > 0 && (
          <Flex gap="1" wrap="wrap">
            {selectedSpecies.map((specie) => (
              <Tag
                key={specie.value}
                label={specie.label}
                size="sm"
                fontWeight="light"
                closeButtonDisplay="always"
                onClose={(): void => {
                  handleSelection({ ...specie, isSelected: false });
                }}
              />
            ))}
          </Flex>
        )}
      </Flex>
    </SegmentWrapper>
  );
};
